import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@repo/ui/primitives/button";
import { Input } from "@repo/ui/primitives/input";
import { Label } from "@repo/ui/primitives/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/primitives/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/primitives/select";
import { useTranslation } from "react-i18next";
import { authIntegration } from "@/lib/api/client";
import { OtpCountdownHint } from "@/components/auth/OtpCountdownHint";
import { useOtpCountdown } from "@/hooks/useOtpCountdown.js";
import { forgotPasswordSendErrorCopy } from "@/lib/auth/forgot-password-send-error.js";

export function ForgotPasswordModal({ isOpen, onClose }) {
  const { t } = useTranslation("login");
  const navigate = useNavigate();
  const [step, setStep] = useState("identity");
  const [identity, setIdentity] = useState("");
  const [channel, setChannel] = useState("email");
  const [code, setCode] = useState("");
  const [maskedDestination, setMaskedDestination] = useState("");
  const [otpMeta, setOtpMeta] = useState({ expiresInSec: 300, resendCooldownSec: 60, maxAttempts: 5 });
  const [issuedAt, setIssuedAt] = useState(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [sendErrorCode, setSendErrorCode] = useState(null);

  const countdown = useOtpCountdown({
    issuedAt: step === "code" ? issuedAt : null,
    expiresInSec: otpMeta.expiresInSec,
    resendCooldownSec: otpMeta.resendCooldownSec,
  });

  const resetState = () => {
    setStep("identity");
    setIdentity("");
    setChannel("email");
    setCode("");
    setMaskedDestination("");
    setMessage("");
    setSendErrorCode(null);
    setIssuedAt(null);
    setAttemptsRemaining(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const applyIssued = (data) => {
    setMaskedDestination(data?.maskedDestination || "");
    setOtpMeta({
      expiresInSec: Number(data?.expiresInSec) || 300,
      resendCooldownSec: Number(data?.resendCooldownSec) || 60,
      maxAttempts: Number(data?.maxAttempts) || 5,
    });
    setIssuedAt(Date.now());
    setAttemptsRemaining(null);
    setCode("");
    setStep("code");
    setMessage("success");
    setSendErrorCode(null);
  };

  const handleSendCode = async (e) => {
    e?.preventDefault?.();
    if (!identity.trim() || !channel) return;
    setIsLoading(true);
    setMessage("");
    setSendErrorCode(null);
    try {
      const result = await authIntegration.forgotPassword({
        identity: identity.trim(),
        channel,
      });
      if (result?.success && result.data?.expiresInSec) {
        applyIssued(result.data);
      } else {
        setSendErrorCode(result?.code || "NOT_ISSUED");
        setMessage("error");
      }
    } catch {
      setSendErrorCode("DELIVERY_FAILED");
      setMessage("error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!countdown.canResend || isLoading) return;
    await handleSendCode();
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setIsLoading(true);
    setMessage("");
    setSendErrorCode(null);
    try {
      const result = await authIntegration.verifyForgotPassword({
        identity: identity.trim(),
        channel,
        code: code.trim(),
      });
      if (result?.success && result.data?.resetToken) {
        handleClose();
        navigate(`/auth/reset-password?token=${encodeURIComponent(result.data.resetToken)}`);
      } else {
        setMessage("error");
        if (typeof result?.attemptsRemaining === "number") {
          setAttemptsRemaining(result.attemptsRemaining);
        }
      }
    } catch {
      setMessage("error");
    } finally {
      setIsLoading(false);
    }
  };

  const channelChoices = [
    { value: "email", label: t("forgotPassword.channelEmail", { defaultValue: "Email" }) },
    { value: "sms", label: t("forgotPassword.channelSms", { defaultValue: "SMS" }) },
  ];
  const sendErrorCopy = sendErrorCode ? forgotPasswordSendErrorCopy(sendErrorCode) : null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("forgotPassword.title", { defaultValue: "Reset Password" })}</DialogTitle>
          <DialogDescription>
            {step === "identity"
              ? t("forgotPassword.description", { defaultValue: "Enter your email or username and choose how to receive a verification code." })
              : t("forgotPassword.codeDescription", {
                defaultValue: "Enter the verification code sent to {{destination}}",
                destination: maskedDestination || "…",
              })}
          </DialogDescription>
        </DialogHeader>

        {step === "identity" ? (
          <form onSubmit={handleSendCode} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-identity">{t("forgotPassword.label", { defaultValue: "Email Address or Username" })}</Label>
              <Input
                id="reset-identity"
                type="text"
                placeholder={t("forgotPassword.placeholder", { defaultValue: "Enter your email or username" })}
                value={identity}
                onChange={(e) => setIdentity(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>{t("forgotPassword.channelLabel", { defaultValue: "Send code via" })}</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {channelChoices.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {message === "error" && sendErrorCopy && (
              <div className="rounded-[var(--ds-radius-control)] border border-destructive/45 bg-destructive/10 p-[var(--ds-space-3)] text-sm text-[var(--destructive-tint-foreground)]">
                {t(sendErrorCopy.key, { defaultValue: sendErrorCopy.defaultValue })}
              </div>
            )}

            <div className="flex justify-end gap-[var(--ds-space-control-gap)]">
              <Button type="button" variant="outline" onClick={handleClose}>
                {t("forgotPassword.cancelButton", { defaultValue: "Cancel" })}
              </Button>
              <Button type="submit" disabled={isLoading || !identity.trim()}>
                {isLoading
                  ? t("forgotPassword.submitButtonLoading", { defaultValue: "Sending..." })
                  : t("forgotPassword.submitButton", { defaultValue: "Send code" })}
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleVerify} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-code">
                {t("forgotPassword.codeLabel", { defaultValue: "Verification code" })}
              </Label>
              <Input
                id="reset-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={8}
                required
                autoFocus
              />
              <OtpCountdownHint
                ns="login"
                active={countdown.active}
                expired={countdown.expired}
                expiresLabel={countdown.expiresLabel}
                canResend={countdown.canResend}
                resendInSec={countdown.resendInSec}
                attemptsRemaining={attemptsRemaining}
                maxAttempts={otpMeta.maxAttempts}
              />
            </div>

            {message === "error" && (
              <div className="rounded-[var(--ds-radius-control)] border border-destructive/45 bg-destructive/10 p-[var(--ds-space-3)] text-sm text-[var(--destructive-tint-foreground)]">
                {sendErrorCopy
                  ? t(sendErrorCopy.key, { defaultValue: sendErrorCopy.defaultValue })
                  : t("forgotPassword.verifyError", {
                    defaultValue: "Invalid or expired code. Please try again.",
                  })}
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-[var(--ds-space-control-gap)]">
              <Button type="button" variant="outline" onClick={() => setStep("identity")}>
                {t("forgotPassword.backButton", { defaultValue: "Back" })}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={isLoading || !countdown.canResend}
                onClick={handleResend}
              >
                {t("mfa.resendButton", { defaultValue: "Resend code" })}
              </Button>
              <Button type="submit" disabled={isLoading || !code.trim() || countdown.expired}>
                {isLoading
                  ? t("forgotPassword.verifyLoading", { defaultValue: "Verifying…" })
                  : t("forgotPassword.verifyButton", { defaultValue: "Continue" })}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
